import React, { useEffect, useState, useCallback } from "react";
import axios from "../../api/axiosAuth";
import { useAuth } from "../../context/AuthContext";
import { API } from "../config"; // ✅ Importación centralizada coherente con los otros módulos

const DashboardRector = () => {
  const { token, user } = useAuth();

  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ===============================
      🔄 CARGA DE DATOS
  =============================== */
  const cargarResumen = useCallback(async () => {
    if (!token || !user?.tenant_id || !user?.sede_id) return;

    try {
      setLoading(true);
      setError(null);

      const res = await axios.get(`${API}/rector/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          tenant_id: user.tenant_id,
          sede_id: user.sede_id,
        },
      });

      setResumen(res.data);
    } catch (err) {
      console.error("❌ Error dashboard:", err.response?.data || err.message);
      setError("No se pudo cargar el panel de control. Por favor, intente nuevamente.");
    } finally {
      setLoading(false);
    }
  }, [token, user?.tenant_id, user?.sede_id]);

  useEffect(() => {
    cargarResumen();
  }, [cargarResumen]);

  /* ================= LOADING SKELETON ================= */
  if (loading) {
    return (
      <div className="p-6 animate-pulse">
        <div className="h-8 w-64 bg-gray-300 rounded mb-6"></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-2xl"></div>
          ))}
        </div>
      </div>
    );
  }

  /* ================= ERROR UI ================= */
  if (error) {
    return (
      <div className="p-6 text-center">
        <div className="bg-red-50 p-4 rounded-lg inline-block">
          <p className="text-red-500 font-semibold mb-4">{error}</p>
          <button
            onClick={cargarResumen}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md"
          >
            Reintentar conexión
          </button>
        </div>
      </div>
    );
  }

  if (!resumen) return null;

  /* ================= MAIN UI ================= */
  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      
      {/* HEADER */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-800">
          🎓 Panel del Rector
        </h2>
        <p className="text-gray-500 text-lg">
          {user?.nombre_sede || "Sede Principal"} — Resumen académico consolidado
        </p>
      </div>

      {/* CARDS INDICADORES */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        
        <Card
          title="Año Lectivo"
          value={resumen.anio_lectivo}
          subtitle={`Periodo actual: ${resumen.periodo || 'N/A'}`}
          color="bg-blue-600"
          icon="📅"
        />

        <Card
          title="Estudiantes"
          value={resumen.total_estudiantes}
          subtitle="Matriculados activos"
          color="bg-green-600"
          icon="👥"
        />

        <Card
          title="Docentes"
          value={resumen.total_docentes}
          subtitle="Planta académica"
          color="bg-orange-500"
          icon="👨‍🏫"
        />

        <Card
          title="Grupos"
          value={resumen.total_grupos}
          subtitle="Salones configurados"
          color="bg-purple-600"
          icon="🏫"
        />

      </div>

      {/* DETALLE TÉCNICO */}
      <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
        <h3 className="text-xl font-semibold text-gray-700 mb-6 flex items-center">
          <span className="mr-2">📊</span> Estadísticas Detalladas
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Detail label="Periodo lectivo" value={resumen.periodo} />
          <Detail label="Año de vigencia" value={resumen.anio_lectivo} />
          <Detail label="Asignaturas cargadas" value={resumen.total_materias} />
          <Detail label="Cursos/Grupos" value={resumen.total_grupos} />
          <Detail label="Población Estudiantil" value={resumen.total_estudiantes} />
          <Detail label="Personal Docente" value={resumen.total_docentes} />
        </div>
      </div>
    </div>
  );
};

/* ================= COMPONENTES INTERNOS ================= */

const Card = ({ title, value, subtitle, color, icon }) => (
  <div className={`${color} text-white p-6 rounded-2xl shadow-lg hover:scale-[1.02] transition-transform duration-300 relative overflow-hidden group`}>
    <div className="relative z-10">
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-lg font-medium opacity-90">{title}</h3>
        <span className="text-2xl">{icon}</span>
      </div>
      <p className="text-4xl font-bold mb-1">{value ?? 0}</p>
      <p className="text-sm opacity-80 font-light">{subtitle}</p>
    </div>
    {/* Efecto decorativo de fondo */}
    <div className="absolute -right-4 -bottom-4 text-white opacity-10 text-8xl group-hover:scale-110 transition-transform">
      {icon}
    </div>
  </div>
);

const Detail = ({ label, value }) => (
  <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex justify-between items-center">
    <span className="text-gray-600 font-medium">{label}</span>
    <span className="text-gray-900 font-bold bg-white px-3 py-1 rounded-md shadow-sm border border-gray-200">
      {value ?? "-"}
    </span>
  </div>
);

export default DashboardRector;